import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DynamicIcon from "../DynamicIcon";
import { selectAccountData } from "../../redux/main/accountSlice";
import { createCustomerInteraction, getCustomerInteractionDetails, selectCustomerInteractionDetails, updateCustomerInteraction } from "../../redux/customerInteractions/customerInteractionsSlice";
import { resetRefreshNow, selectRightModalmode, setHasToRefresh } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";
import CustomSelectElement from "../CustomSelectElement";
import CustomerSelect from "../CustomerSelect";
import AddCustomerModal from "./AddCustomerModal";

export default function AddEditCustomerInteraction({ interactionId }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const interactionDetails = useSelector(selectCustomerInteractionDetails);
    const modalMode = useSelector(selectRightModalmode);
    const token = localStorage.getItem('installamelo-auth-token');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        customerId: '',
        customerLabel: '',
        typeId: '',
        typeLabel: '',
        interactionMethodTypeId: '',
        interactionMethodTypeLabel: '',
        notes: '',
        files: [],
        partecipants: []
    });

    // Quando viene selezionato un cliente dal componente CustomerSelect
    const handleCustomerSelect = (option) => {
        if (option) {
            setSelectedCustomer(option);
            setFormData(prevState => ({
                ...prevState,
                customerId: option.value,
                customerLabel: option.label
            }));
        }
    };

    // Initialize form data based on edit mode
    useEffect(() => {
        if (modalMode === 'edit' && interactionId) {
            setIsLoading(true);
            dispatch(getCustomerInteractionDetails({ interactionId, token }))
                .then(() => setIsLoading(false))
                .catch(() => {
                    toast.error("Errore nel caricamento dei dettagli dell'interazione");
                    setIsLoading(false);
                });
        }
        dispatch(resetRefreshNow());
    }, [dispatch, interactionId, modalMode, token]);

    // Populate form when interactionDetails is loaded
    useEffect(() => {
        if (interactionDetails && modalMode === 'edit') {
            setFormData({
                customerId: interactionDetails.customerId || '',
                customerLabel: interactionDetails.customerLabel || '',
                typeId: interactionDetails.typeId || '',
                typeLabel: interactionDetails.typeLabel || '',
                interactionMethodTypeId: interactionDetails.interactionMethodTypeId || '',
                interactionMethodTypeLabel: interactionDetails.interactionMethodTypeLabel || '',
                notes: interactionDetails.notes || '',
                files: interactionDetails.files || [],
                partecipants: interactionDetails.partecipants || []
            });
            
            // Pre-popolamento del CustomerSelect con il cliente esistente
            if (interactionDetails.customerId && interactionDetails.customerLabel) {
                setSelectedCustomer({
                    value: interactionDetails.customerId,
                    label: interactionDetails.customerLabel
                });
            }
        }
    }, [interactionDetails, modalMode]);

    // Form change handler
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    // Type select handler
    const handleTypeSelect = (value, label) => {
        setFormData(prevState => ({
            ...prevState,
            typeId: value,
            typeLabel: label
        }));
    };

    // Method type select handler
    const handleMethodTypeSelect = (value, label) => {
        setFormData(prevState => ({
            ...prevState,
            interactionMethodTypeId: value,
            interactionMethodTypeLabel: label
        }));
    };

    // Form submission handler
    const handleSubmit = (e) => {
        e.preventDefault();
        
        setIsLoading(true);
        
        const interactionData = {
            ...formData,
            accountId: accountData._id,
            userId: accountData.userId,
            userLabel: accountData.userLabel,
            creationDate: new Date()
        };
        
        // If we're in edit mode, include the ID
        if (modalMode === 'edit' && interactionDetails) {
            interactionData._id = interactionDetails._id;
        }
        
        const action = modalMode === 'add' 
            ? createCustomerInteraction({ customerInteraction: interactionData, token })
            : updateCustomerInteraction({ customerInteraction: interactionData, token });
        
        dispatch(action)
            .then(() => {
                const successMessage = modalMode === 'add' 
                    ? "Interazione creata con successo" 
                    : "Interazione aggiornata con successo";
                toast.success(successMessage);
                dispatch(setHasToRefresh());
                setIsLoading(false);
            })
            .catch((err) => {
                const errorMessage = modalMode === 'add' 
                    ? "Errore nella creazione dell'interazione" 
                    : "Errore nell'aggiornamento dell'interazione";
                toast.error(errorMessage);
                setIsLoading(false);
            });
    };

    // Prepare types list from account settings
    const interactionTypes = accountData?.settings?.customerInteractionTypes?.map(type => ({
        value: type.id,
        label: type.label
    })) || [];

    // Prepare method types list from account settings
    const interactionMethodTypes = accountData?.settings?.customerInteractionMethodTypes?.map(type => ({
        value: type.id,
        label: type.label
    })) || [];

    return (
        <div className="flex flex-col h-full">
            {isLoading && <LoadingSection />}
            
            <div className="flex-1 overflow-auto p-4">
                <h2 className="text-xl font-semibold mb-4">
                    {modalMode === 'add' ? 'Nuova Interazione' : 'Modifica Interazione'}
                </h2>
                
                <form onSubmit={handleSubmit}>
                    {/* Cliente */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                        <div className="flex items-center gap-2">
                            <div className="flex-grow">
                                <CustomerSelect 
                                    selectedOption={selectedCustomer}
                                    setSelectedOption={handleCustomerSelect}
                                    showSelectedLabel={true}
                                />
                            </div>
                            <button 
                                type="button"
                                className="flex items-center justify-center bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600 transition-colors"
                                onClick={() => setShowAddCustomerModal(true)}
                                title="Aggiungi nuovo cliente"
                            >
                                <DynamicIcon iconClass="fa-solid fa-plus" />
                            </button>
                        </div>
                    </div>
                    
                    {/* Modale per aggiungere un nuovo cliente */}
                    {showAddCustomerModal && (
                        <AddCustomerModal 
                            onClose={() => setShowAddCustomerModal(false)}
                            onCustomerAdded={(newCustomer) => {
                                // Imposta il cliente appena aggiunto nella select
                                handleCustomerSelect({
                                    value: newCustomer._id,
                                    label: newCustomer.business ? newCustomer.company : `${newCustomer.name} ${newCustomer.surname}`
                                });
                                setShowAddCustomerModal(false);
                            }}
                        />
                    )}
                    
                    {/* Tipo di interazione */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo di interazione</label>
                        <CustomSelectElement
                            id="type-select"
                            name="type"
                            list={interactionTypes}
                            setter={handleTypeSelect}
                            defaultValue={formData.typeId}
                            valueKey="value"
                            labelKey="label"
                        />
                    </div>
                    
                    {/* Metodo di interazione */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Metodo di interazione</label>
                        <CustomSelectElement
                            id="method-type-select"
                            name="methodType"
                            list={interactionMethodTypes}
                            setter={handleMethodTypeSelect}
                            defaultValue={formData.interactionMethodTypeId}
                            valueKey="value"
                            labelKey="label"
                        />
                    </div>
                    
                    {/* Note */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Note</label>
                        <textarea
                            id="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            rows="5"
                        />
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="submit"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={isLoading}
                        >
                            <DynamicIcon iconClass="fa-solid fa-save mr-2" />
                            {modalMode === 'add' ? 'Crea' : 'Aggiorna'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
